import { useCallback, useEffect, useState } from 'react';

import AcademiaSelect from '../../common/AcademiaSelect';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import AdminToast from '../AdminToast';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import { fetchAdminWalletOverview, grantAcademicWalletCredit } from '../../../services/walletService';
import { formatMoney } from '../../../utils/currency';
import {
  AcademicPanelCard,
  AcademicPanelListItem,
  AcademicPanelTableWrap,
} from './AcademicPanelCard';
import AcademiaPendingWithdrawalsSection from './AcademiaPendingWithdrawalsSection';

const fieldInputClass =
  'tarif-field__input w-full min-w-0 rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

const ACADEMIC_CREDIT_MOTIFS = [
  { value: 'Bourse / crédit académique', label: 'Bourse / crédit académique' },
  { value: "Bourse d'excellence", label: "Bourse d'excellence" },
  { value: 'Aide sociale — cas particulier', label: 'Aide sociale — cas particulier' },
  { value: 'Compensation administrative', label: 'Compensation administrative' },
  { value: 'Remboursement / régularisation', label: 'Remboursement / régularisation' },
  { value: 'Prime institutionnelle / partenariat', label: 'Prime institutionnelle / partenariat' },
  { value: 'Geste commercial', label: 'Geste commercial' },
] as const;

const DEFAULT_ACADEMIC_CREDIT_MOTIF = ACADEMIC_CREDIT_MOTIFS[0].value;

type ToastState = { message: string; variant: 'success' | 'error' } | null;

type KpiTone = 'primary' | 'amber' | 'emerald';

const KPI_TONE_CLASS: Record<KpiTone, string> = {
  primary: 'border-outline-variant/25 bg-gradient-to-br from-white to-surface-container-low',
  amber: 'border-primary/15 bg-gradient-to-br from-primary/[0.06] to-white',
  emerald: 'border-emerald-200/50 bg-gradient-to-br from-emerald-50/70 to-white',
};

const KPI_LABEL_CLASS: Record<KpiTone, string> = {
  primary: 'text-on-surface-variant',
  amber: 'text-primary',
  emerald: 'text-emerald-800',
};

const KPI_VALUE_CLASS: Record<KpiTone, string> = {
  primary: 'text-primary',
  amber: 'text-primary',
  emerald: 'text-emerald-900',
};

function WalletKpiTile({
  label,
  value,
  hint,
  icon,
  tone = 'primary',
  href,
}: {
  label: string;
  value: string | number;
  hint?: string;
  icon: string;
  tone?: KpiTone;
  href?: string;
}) {
  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className={`text-xs font-bold uppercase tracking-wide ${KPI_LABEL_CLASS[tone]}`}>{label}</p>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/90 text-primary shadow-sm ring-1 ring-outline-variant/10">
          <LucideSymbolIcon name={icon} tone="inherit" size={18} />
        </span>
      </div>
      <p className={`mt-2 text-3xl font-black tabular-nums leading-none ${KPI_VALUE_CLASS[tone]}`}>{value}</p>
      {hint ? <p className={`mt-2 text-xs leading-snug ${KPI_LABEL_CLASS[tone]}`}>{hint}</p> : null}
    </>
  );

  const className = [
    'rounded-xl border p-4 shadow-sm transition duration-200',
    KPI_TONE_CLASS[tone],
    href ? 'hover:border-primary/30 hover:shadow-md' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (href) {
    return (
      <a href={href} className={`block ${className}`}>
        {content}
      </a>
    );
  }

  return <article className={className}>{content}</article>;
}

function roleLabel(role: string) {
  if (role === 'APPRENANT') return 'Apprenant';
  if (role === 'FORMATEUR') return 'Formateur';
  if (role === 'PARTENAIRE_INSTITUTIONNEL') return 'Partenaire';
  return role;
}

type Overview = {
  synthese?: {
    portefeuillesActifs: number;
    soldeTotalCdf: number;
    retraitsEnAttente: number;
  };
  topSoldes?: {
    userId: string;
    solde: number;
    devise: string;
    role: string;
    nom: string;
  }[];
  creditsRecents?: {
    id: string;
    montant: number;
    date: string;
    beneficiaire: string;
    role: string;
    motif: string;
  }[];
};

export default function AcademiaWalletAdminPanel() {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [codeParrainage, setCodeParrainage] = useState('');
  const [montant, setMontant] = useState('');
  const [motif, setMotif] = useState<string>(DEFAULT_ACADEMIC_CREDIT_MOTIF);
  const [granting, setGranting] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      setData((await fetchAdminWalletOverview()) as Overview);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur chargement portefeuilles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const s = data?.synthese;
  const topSoldes = data?.topSoldes ?? [];
  const {
    paginatedItems: topSoldesPage,
    currentPage: topSoldesPageNum,
    totalPages: topSoldesTotalPages,
    onPageChange: onTopSoldesPageChange,
    summary: topSoldesSummary,
  } = useAdminTablePagination(topSoldes);

  const creditsRecents = data?.creditsRecents ?? [];
  const {
    paginatedItems: creditsRecentsPage,
    currentPage: creditsRecentsPageNum,
    totalPages: creditsRecentsTotalPages,
    onPageChange: onCreditsRecentsPageChange,
    summary: creditsRecentsSummary,
  } = useAdminTablePagination(creditsRecents);

  return (
    <AdminPageShell
      title="Portefeuilles Academia"
      description="Soldes apprenants, formateurs et partenaires — crédits académiques et retraits."
      sideCard={{
        label: 'Solde total plateforme',
        value: loading ? '…' : formatMoney(s?.soldeTotalCdf ?? 0, 'CDF'),
        helper: s ? `${s.retraitsEnAttente} retrait(s) en attente` : undefined,
        sideLinks: [
          { label: 'Retraits en attente', to: '/admin/wallet#retraits-en-attente', icon: 'account_balance_wallet' },
          { label: 'Parrainage', to: '/admin/parrainage', icon: 'groups' },
        ],
      }}
      isLoading={loading && !data}
      isError={Boolean(error) && !data}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      loadingMessage="Chargement des portefeuilles…"
    >
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
      <div className="space-y-6">
        {error && !data ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
        ) : null}

        {data ? (
          <section className="grid gap-4 sm:grid-cols-3">
            <WalletKpiTile
              label="Portefeuilles actifs"
              value={s?.portefeuillesActifs ?? 0}
              hint="Comptes avec solde ou mouvements"
              icon="account_balance_wallet"
              tone="primary"
            />
            <WalletKpiTile
              label="Retraits en attente"
              value={s?.retraitsEnAttente ?? 0}
              hint="Cliquez pour traiter la file"
              icon="pending_actions"
              tone="amber"
              href="#retraits-en-attente"
            />
            <WalletKpiTile
              label="Solde cumulé"
              value={formatMoney(s?.soldeTotalCdf ?? 0, 'CDF')}
              hint="Tous portefeuilles Academia confondus"
              icon="account_balance"
              tone="emerald"
            />
          </section>
        ) : null}

        <AcademiaPendingWithdrawalsSection
          sectionId="retraits-en-attente"
          onChanged={() => void load()}
          showToast={showToast}
        />

        <AcademiaPendingWithdrawalsSection
          status="APPROUVEE"
          sectionId="retraits-approuves"
          variant="success"
          onChanged={() => void load()}
          showToast={showToast}
        />

        <AcademiaPendingWithdrawalsSection
          status="EN_PAIEMENT"
          title="Payouts en cours"
          variant="default"
          onChanged={() => void load()}
          showToast={showToast}
        />

        <AcademiaPendingWithdrawalsSection
          status="ECHEC_PAIEMENT"
          title="Payouts en échec"
          variant="accent"
          onChanged={() => void load()}
          showToast={showToast}
        />

        {data ? (
          <>
            <AcademicPanelCard
              variant="accent"
              icon="stars"
              title="Accorder un crédit académique"
              subtitle="Saisissez le code de parrainage de l'apprenant ou du formateur — le montant est crédité sur son portefeuille."
              footer={
                <button
                  type="button"
                  disabled={granting || !codeParrainage.trim() || !montant}
                  className="academic-panel-btn academic-panel-btn--primary"
                  onClick={async () => {
                    setGranting(true);
                    try {
                      const res = await grantAcademicWalletCredit({
                        codeParrainage: codeParrainage.trim(),
                        montant: Number(montant),
                        motif: motif.trim(),
                      });
                      showToast(res?.message ?? 'Crédit académique accordé.', 'success');
                      setCodeParrainage('');
                      setMontant('');
                      setMotif(DEFAULT_ACADEMIC_CREDIT_MOTIF);
                      await load();
                    } catch (e) {
                      showToast(e instanceof Error ? e.message : "Impossible d'accorder le crédit.", 'error');
                    } finally {
                      setGranting(false);
                    }
                  }}
                >
                  <LucideSymbolIcon name="add_circle" tone="inherit" size={16} />
                  {granting ? 'En cours…' : 'Créditer le portefeuille'}
                </button>
              }
            >
              <div className="academic-panel-toolbar academic-panel-toolbar--spread !border-0 !bg-transparent !p-0">
                <label className="tarif-field">
                  <span className="tarif-field__label">Code parrainage</span>
                  <input
                    className={`${fieldInputClass} font-mono uppercase tracking-wide`}
                    value={codeParrainage}
                    onChange={(e) => setCodeParrainage(e.target.value.toUpperCase().replace(/\s/g, ''))}
                    placeholder="Ex. JA3XK2F"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </label>
                <label className="tarif-field">
                  <span className="tarif-field__label">Montant CDF</span>
                  <input
                    type="number"
                    min={1}
                    className={`${fieldInputClass} tabular-nums`}
                    value={montant}
                    onChange={(e) => setMontant(e.target.value)}
                    placeholder="Ex. 50 000"
                  />
                </label>
                <label className="tarif-field">
                  <span className="tarif-field__label">Motif</span>
                  <AcademiaSelect
                    value={motif}
                    onChange={(e) => setMotif(e.target.value)}
                    aria-label="Motif du crédit académique"
                  >
                    {ACADEMIC_CREDIT_MOTIFS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AcademiaSelect>
                </label>
              </div>
            </AcademicPanelCard>

            <AcademicPanelCard
              icon="trending_up"
              title="Top soldes"
              subtitle="Utilisateurs avec les soldes portefeuille les plus élevés."
              badge={
                topSoldes.length > 0 ? (
                  <span className="academic-panel-badge">{topSoldes.length} compte(s)</span>
                ) : undefined
              }
              flush
            >
              <AcademicPanelTableWrap
                empty={
                  topSoldesPage.length === 0 ? (
                    <p className="academic-panel-empty">Aucun portefeuille enregistré.</p>
                  ) : undefined
                }
              >
                {topSoldesPage.length > 0 ? (
                  <table className="academic-panel-table">
                    <thead>
                      <tr>
                        <th>Utilisateur</th>
                        <th>Rôle</th>
                        <th className="text-right">Solde</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSoldesPage.map((row) => (
                        <tr key={row.userId}>
                          <td className="font-semibold text-slate-900">{row.nom}</td>
                          <td>
                            <span className="academic-panel-badge academic-panel-badge--muted">
                              {roleLabel(row.role)}
                            </span>
                          </td>
                          <td className="text-right font-black text-primary tabular-nums">
                            {formatMoney(row.solde, row.devise)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}
              </AcademicPanelTableWrap>
              {topSoldes.length > 0 ? (
                <AdminPagination
                  className="border-t border-outline-variant/15 px-4 py-3"
                  currentPage={topSoldesPageNum}
                  totalPages={topSoldesTotalPages}
                  onPageChange={onTopSoldesPageChange}
                  summary={topSoldesSummary}
                />
              ) : null}
            </AcademicPanelCard>

            <AcademicPanelCard
              variant="success"
              icon="history"
              title="Crédits académiques récents"
              subtitle="Derniers crédits manuels accordés par l'administration."
              badge={
                creditsRecents.length > 0 ? (
                  <span className="academic-panel-badge academic-panel-badge--success">
                    {creditsRecents.length} opération(s)
                  </span>
                ) : undefined
              }
            >
              {creditsRecents.length === 0 ? (
                <p className="text-sm text-on-surface-variant">Aucun crédit académique enregistré.</p>
              ) : (
                <ul className="list-none p-0">
                  {creditsRecentsPage.map((c) => (
                    <AcademicPanelListItem key={c.id}>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900">{c.beneficiaire}</p>
                        <p className="mt-0.5 text-xs text-on-surface-variant">
                          {c.motif} · {roleLabel(c.role)}
                        </p>
                      </div>
                      <p className="shrink-0 font-black text-primary tabular-nums">
                        {formatMoney(c.montant, 'CDF')}
                      </p>
                    </AcademicPanelListItem>
                  ))}
                </ul>
              )}
              {creditsRecents.length > 0 ? (
                <AdminPagination
                  className="mt-4 border-t border-outline-variant/15 pt-3"
                  currentPage={creditsRecentsPageNum}
                  totalPages={creditsRecentsTotalPages}
                  onPageChange={onCreditsRecentsPageChange}
                  summary={creditsRecentsSummary}
                />
              ) : null}
            </AcademicPanelCard>
          </>
        ) : null}
      </div>
    </AdminPageShell>
  );
}
